import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Field, StatusPill, Toggle } from "@ozpanel/kit";
import { Button, ConfirmDialog, Panel, StateView, useAuth, useToast } from "@ozpanel/ui";

// The node REGISTRY verbs: register, edit, deregister (internal/modules/cluster/cluster.go —
// handleRegisterNode, handleUpdateNode, handleDeleteNode). They were reachable only through the
// generic operations form, which cannot say which fields a PATCH accepts on which node, nor what a
// deregistration leaves behind.
//
// None of the three is master-only: each is super_admin on ANY node (the registry is a Galera table),
// so nothing here is gated on the master. What IS gated, the server gates per field or per node, and
// each gate is mirrored where the operator meets it:
//   - floating_ip and real_ip are written to the node's OWN configuration file, so PATCH refuses them
//     for any node but the one answering (ensureSelfNode would re-assert the config at the next start
//     and silently undo a registry-only edit);
//   - DELETE refuses the node answering ("cannot deregister this node").

/** GET /cluster/nodes/{name} — nodeResp's json tags, exactly. */
export interface RegistryNode {
  name: string;
  uuid?: string;
  floating_ip: string;
  real_ip: string;
  lan_gateway?: string;
  role: string;
  fqdn?: string;
  version?: string;
  status: string;
  self: boolean;
  last_seen: string;
  region: string;
  public_ip: string;
  label?: string;
  /** The configured mesh listen port (cluster setting tinc.mesh_port.<node>); absent = not set (656). */
  mesh_port?: string;
  /** Set when that setting could not be read — never the same as "not set". */
  mesh_port_error?: string;
  // The NAT egress address is also on the wire; it is not read here (see EditKey).
}

/** POST /cluster/nodes — the map handleRegisterNode answers with. */
interface RegisterAnswer {
  node_name: string;
  node_uuid: string;
  registered: boolean;
  galera: string;
  install_with: string;
  dns?: string[] | null;
  dns_failed?: string[] | null;
  dns_note?: string;
}

const ipLike = (v: string) => /^(\d{1,3}\.){3}\d{1,3}$/.test(v) || (v.includes(":") && /^[0-9a-fA-F:.]+$/.test(v));
// cluster.go regionRe.
const regionRe = /^[a-z0-9][a-z0-9-]{0,31}$/;
// tinc.ValidMeshPort: a port number 1–65535.
const validPort = (v: string) => /^\d{1,5}$/.test(v) && Number(v) >= 1 && Number(v) <= 65535;

function CopyLine({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div>
      <div className="text-[11.5px] tracking-wide text-muted uppercase">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="min-w-0 flex-1 rounded border border-line bg-bg px-2 py-1.5 font-mono text-[12px] break-all select-all">{value}</code>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            void navigator.clipboard
              ?.writeText(value)
              .then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              })
              .catch(() => undefined)
          }
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
}

// ── Register ─────────────────────────────────────────────────────────────────────────────────

export function RegisterNodePanel({ existing, onClose }: { existing: string[]; onClose: () => void }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = React.useState("");
  const [fqdn, setFqdn] = React.useState("");
  const [realIP, setRealIP] = React.useState("");
  const [floatingIP, setFloatingIP] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [answer, setAnswer] = React.useState<RegisterAnswer | null>(null);

  const n = name.trim();
  // Re-registering a name is an UPSERT on the server: the identity is kept, but its role and fqdn are
  // overwritten and its interface and floating addresses are re-recorded from THIS form — blank
  // included. That is an edit wearing a registration's clothes, so it is sent to Edit instead.
  const taken = n !== "" && existing.includes(n);
  const why =
    n === ""
      ? "A node name is required."
      : taken
        ? `${n} is already registered. Registering it again keeps its identity but overwrites its recorded interface and floating addresses with what is typed here (blank clears them) — use Edit on its row instead.`
        : realIP.trim() !== "" && !ipLike(realIP.trim())
          ? "Interface address: not an IP address."
          : floatingIP.trim() !== "" && !ipLike(floatingIP.trim())
            ? "Floating address: not an IP address."
            : null;

  const submit = () => {
    setBusy(true);
    setErr(null);
    void client
      .apiFetch<RegisterAnswer>("/cluster/nodes", {
        method: "POST",
        body: JSON.stringify({ node_name: n, fqdn: fqdn.trim(), real_ip: realIP.trim(), floating_ip: floatingIP.trim() }),
      })
      .then((a) => {
        setAnswer(a);
        void qc.invalidateQueries({ queryKey: ["cluster-nodes"] });
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Panel className="m-4 space-y-3 p-4">
      <div className="flex items-center gap-2 text-[13px] font-medium">
        Register a node
        <button type="button" className="ml-auto text-[11.5px] font-normal text-muted hover:text-fg" onClick={onClose}>
          close
        </button>
      </div>
      <p className="text-[12px] leading-relaxed text-muted">
        Registering only <b>records</b> a node: it mints the node's identity (its UUID) and puts it in the registry, so
        zones and the Galera peer list name it. Nothing is installed and no machine is contacted. To bring a real machine
        into the cluster — over SSH, every install step, its certificate, the mesh and storage — use{" "}
        <Link to="/nodes/add" className="text-accent hover:underline">
          Add a node
        </Link>{" "}
        instead. Register is for a machine you will install yourself (<span className="font-mono">install-node.sh</span>{" "}
        / <span className="font-mono">ozpanel init-config</span>), which must then run under the identity printed here.
      </p>

      {answer ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[12.5px]">
            <StatusPill tone="ok">registered</StatusPill>
            <span className="font-mono">{answer.node_name}</span>
          </div>
          <CopyLine label="Node UUID (its identity)" value={answer.node_uuid} />
          <CopyLine label="Install the machine with" value={answer.install_with} />
          <p className="text-[11.5px] leading-relaxed text-muted">
            The machine must run under this identity: installed with the line above, or adopting it at its first start
            (a node whose configuration has no UUID adopts the registry's).
          </p>
          <div className="text-[12px] leading-relaxed">
            <div className="text-[11.5px] tracking-wide text-muted uppercase">DNS</div>
            {answer.dns && answer.dns.length > 0 && <div>Republished: {answer.dns.join(", ")}</div>}
            {answer.dns_failed && answer.dns_failed.length > 0 && <div className="text-crit">Failed: {answer.dns_failed.join("; ")}</div>}
            {answer.dns_note && <div className="text-muted">{answer.dns_note}</div>}
            {!answer.dns && !answer.dns_failed && !answer.dns_note && <div className="text-muted">not reported</div>}
          </div>
          <div className="text-[12px] leading-relaxed">
            <div className="text-[11.5px] tracking-wide text-muted uppercase">Galera peer list</div>
            <div className={`font-mono text-[11.5px] break-all ${answer.galera.startsWith("reconcile FAILED") ? "text-crit" : "text-muted"}`}>
              {answer.galera || "not reported"}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setAnswer(null);
              setName("");
              setFqdn("");
              setRealIP("");
              setFloatingIP("");
            }}
          >
            Register another
          </Button>
        </div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Node name" value={name} onChange={setName} mono placeholder="node12" help="The short name every other node and record uses." />
            <Field label="FQDN" value={fqdn} onChange={setFqdn} mono placeholder="(none)" help="The node's own fully-qualified name. The node reports its own at its first start." />
            <Field
              label="Interface address"
              value={realIP}
              onChange={setRealIP}
              mono
              placeholder="(none)"
              help="The address on the machine's own interface (behind NAT, its LAN address). Recorded as its interface address."
            />
            <Field
              label="Floating address"
              value={floatingIP}
              onChange={setFloatingIP}
              mono
              placeholder="(none — direct)"
              help="Only for a node published behind a floating-IP tunnel."
            />
          </div>
          <p className="text-[11.5px] leading-relaxed text-muted">
            Its registry role is recorded as <span className="font-mono">standby</span>. That column is historical only:
            the workload role shown in the list always comes from the signed declaration.
          </p>
          {why && n !== "" && <div className="rounded border border-line bg-bg2 px-3 py-2 text-[12px] text-muted">{why}</div>}
          {err && <div className="rounded border border-line bg-critsoft px-3 py-2 text-[12px] text-crit">{err}</div>}
          <div className="flex justify-end">
            <Button size="sm" variant="accent" disabled={!!why || busy} onClick={submit} title={why ?? undefined}>
              {busy ? "Registering…" : "Register"}
            </Button>
          </div>
        </>
      )}
    </Panel>
  );
}

// ── Edit ─────────────────────────────────────────────────────────────────────────────────────

// nat_egress_ip is accepted by the PATCH and deliberately NOT offered (internal/console/fieldreach.go):
// the node MEASURES it against its peers and its own reconcile writes it, so a typed value would be
// overwritten by the next measurement. It is shown, measured reach included, in the Members list's
// Addresses column (kind nat_egress); naming the field here would mark it reached for that ratchet.
type EditKey = "label" | "region" | "public_ip" | "floating_ip" | "real_ip" | "lan_gateway" | "mesh_port";

export function EditNodePanel({ name, onClose }: { name: string; onClose: () => void }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["cluster-node-settings", name],
    queryFn: () => client.apiFetch<RegistryNode>(`/cluster/nodes/${encodeURIComponent(name)}`),
    retry: false,
  });
  const [vals, setVals] = React.useState<Record<EditKey, string> | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  // The form is filled from what the node registry says NOW. Until that is read there is no form —
  // blank boxes would read as "nothing set" and a save would send them.
  const loaded: Record<EditKey, string> | null = q.data
    ? {
        label: q.data.label ?? "",
        region: q.data.region ?? "",
        public_ip: q.data.public_ip ?? "",
        floating_ip: q.data.floating_ip ?? "",
        real_ip: q.data.real_ip ?? "",
        lan_gateway: q.data.lan_gateway ?? "",
        mesh_port: q.data.mesh_port ?? "",
      }
    : null;
  React.useEffect(() => {
    if (loaded && vals === null) setVals(loaded);
  }, [loaded, vals]);

  const self = q.data?.self === true;
  const set = (k: EditKey) => (v: string) => setVals((cur) => (cur ? { ...cur, [k]: v } : cur));
  // PATCH semantics: only supplied fields change, so only CHANGED fields are sent.
  const changed: Partial<Record<EditKey, string>> = {};
  if (vals && loaded) {
    for (const k of Object.keys(vals) as EditKey[]) {
      if (vals[k].trim() !== loaded[k]) changed[k] = vals[k].trim();
    }
  }
  const nChanged = Object.keys(changed).length;
  const problem = (() => {
    if (!vals) return null;
    const v = (k: EditKey) => vals[k].trim();
    if (v("region") !== "" && !regionRe.test(v("region").toLowerCase())) return "Region: lowercase letters, digits and dashes, e.g. eu-central.";
    if (v("label").length > 64) return "Label: at most 64 characters.";
    for (const k of ["public_ip", "floating_ip", "real_ip", "lan_gateway"] as EditKey[]) {
      if (k in changed && v(k) !== "" && !ipLike(v(k))) return `${k.replace(/_/g, " ")}: not an IP address.`;
    }
    if ("real_ip" in changed && v("real_ip") === "") return "Interface address: cannot be cleared — it must be an address on this node.";
    if ("mesh_port" in changed && v("mesh_port") !== "" && !validPort(v("mesh_port"))) return "Mesh port: a port number 1–65535, or empty for the default 656.";
    if ("mesh_port" in changed && q.data?.mesh_port_error) return "Mesh port: the current setting could not be read, so a change cannot be compared with it — reload and try again.";
    return null;
  })();
  const nodeOnly = self ? undefined : `Only on ${name} itself: the value lives in that node's configuration file as well as the registry, so the server refuses it from any other node. Open ${name}'s console to change it.`;

  const save = () => {
    setBusy(true);
    setErr(null);
    void client
      .apiFetch<RegistryNode>(`/cluster/nodes/${encodeURIComponent(name)}`, { method: "PATCH", body: JSON.stringify(changed) })
      .then((n) => {
        notify({ kind: "success", title: `${name}: ${nChanged} setting(s) saved` });
        setVals(null);
        void qc.invalidateQueries({ queryKey: ["cluster-nodes"] });
        void qc.invalidateQueries({ queryKey: ["cluster-node", name] });
        qc.setQueryData(["cluster-node-settings", name], n);
        onClose();
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Panel className="mb-4 space-y-3 p-4">
      <div className="flex items-center gap-2 text-[13px] font-medium">
        Edit {name}
        <button type="button" className="ml-auto text-[11.5px] font-normal text-muted hover:text-fg" onClick={onClose}>
          close
        </button>
      </div>
      <StateView loading={q.isLoading} error={q.error} errorContext={`${name}'s settings`}>
        {vals && (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Label" value={vals.label} onChange={set("label")} placeholder="(none)" help="Friendly display name, up to 64 characters." />
              <Field
                label="Region"
                value={vals.region}
                onChange={set("region")}
                mono
                placeholder="(none)"
                help="Node geography, read by per-vhost placement in region mode."
              />
              <Field
                label="Public address"
                value={vals.public_ip}
                onChange={set("public_ip")}
                mono
                placeholder="(none)"
                help="Published in A records for sites placed on this node. Any node may set it."
                consequence={"public_ip" in changed ? "Peers derive their firewall and mesh from this address; the Galera peer list is reconciled on save." : undefined}
              />
              <div className="text-[11.5px] leading-relaxed text-muted">
                The NAT egress address is not edited here: the node measures it against its peers and its own reconcile
                writes it, so a typed value would be overwritten. It is listed with its measured reach in the Addresses
                column.
              </div>
              <Field
                label="Floating address"
                value={vals.floating_ip}
                onChange={set("floating_ip")}
                mono
                disabled={!self}
                placeholder="(none — direct)"
                help={nodeOnly ?? "Where the node is published when it sits behind a floating-IP tunnel; written to this node's configuration and the registry. Empty means direct."}
                consequence={self && "floating_ip" in changed ? "Changes the address this node is published at." : undefined}
              />
              <Field
                label="Interface address"
                value={vals.real_ip}
                onChange={set("real_ip")}
                mono
                disabled={!self}
                help={nodeOnly ?? "This node's own interface address (behind NAT, its LAN address). Must be assigned to an interface here — the server checks."}
              />
              <Field
                label="LAN gateway"
                value={vals.lan_gateway}
                onChange={set("lan_gateway")}
                mono
                disabled={!self}
                placeholder="(as recorded at setup)"
                help={nodeOnly ?? "For a floating node behind NAT: the LAN router it leaves by. Its hub route, direct mesh peer routes and tinc-up use it. Must be on one of this node's networks. Empty = what tinc-up recorded at setup."}
                consequence={self && "lan_gateway" in changed ? "Moves this node's hub and peer routes to this router on the next pass; the hub route is put back if the hub stops answering." : undefined}
              />
              <Field
                label="Mesh port"
                value={vals.mesh_port}
                onChange={set("mesh_port")}
                mono
                placeholder="656 (default)"
                help={
                  q.data?.mesh_port_error
                    ? `Not read: ${q.data.mesh_port_error}`
                    : "The port this node's cluster-mesh tinc listens on. Give each node its own when several sit behind one NAT address, so peers can tell them apart. Any node may set it. Empty = the default 656. What tinc actually listens on is shown on the Tinc module's mesh view."
                }
                consequence={
                  "mesh_port" in changed
                    ? `On its next mesh pass ${name} RESTARTS its tinc onto ${vals.mesh_port.trim() || "656"}, and every peer rewrites its host file to dial that port; its firewall opens it from tinc.conf. A node behind NAT is reachable on it only once the router forwards it — otherwise ${name} loses the mesh until the port is put back.`
                    : undefined
                }
              />
            </div>
            {problem && <div className="rounded border border-line bg-bg2 px-3 py-2 text-[12px] text-muted">{problem}</div>}
            {err && <div className="rounded border border-line bg-critsoft px-3 py-2 text-[12px] text-crit">{err}</div>}
            <div className="flex items-center justify-end gap-2">
              <span className="text-[11.5px] text-muted">{nChanged === 0 ? "nothing changed" : `${nChanged} field(s) will be sent`}</span>
              <Button size="sm" variant="accent" disabled={nChanged === 0 || !!problem || busy} onClick={save}>
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </>
        )}
      </StateView>
    </Panel>
  );
}

// ── Deregister ───────────────────────────────────────────────────────────────────────────────

/** DELETE /cluster/nodes/{name} answers {deleted, galera}. */
interface DeregisterAnswer {
  deleted: string;
  galera: string;
}

export function DeregisterDialog({
  node,
  onClose,
  canEvict,
  masterName,
}: {
  node: RegistryNode | null;
  onClose: () => void;
  canEvict: boolean;
  masterName?: string;
}) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [busy, setBusy] = React.useState(false);
  const [ack, setAck] = React.useState(false);
  React.useEffect(() => setAck(false), [node?.name]);
  // A node that has reported a version has run ozpanel: it is (or was) a member, and removing it is
  // EVICTION's job. Deregistering does not revoke it, tombstone it or touch its storage, mesh, Redis
  // or PostgreSQL membership — and the node puts its own row back the next time its panel starts.
  const member = !!node?.version;

  return (
    <ConfirmDialog
      open={!!node}
      onOpenChange={(o) => !o && onClose()}
      title={`Deregister ${node?.name ?? ""}?`}
      confirmLabel={busy ? "Deregistering…" : "Deregister"}
      destructive
      pending={busy}
      confirmDisabled={busy || (member && !ack)}
      body={
        <div className="space-y-3 text-[12.5px] leading-relaxed">
          <p>
            Deletes its row from the node registry and re-derives the Galera peer list. That is all it does: its
            certificates are <b>not</b> revoked, no tombstone is written, and its storage, mesh, Redis and PostgreSQL
            membership are left as they are. Its recorded addresses stay in the address table under its identity.
          </p>
          <p className="text-muted">
            Use it to take back a registration for a machine that never ran ozpanel — a mistaken name, a machine that was
            never installed. The server refuses only the node answering this console.
          </p>
          {member && (
            <div className="space-y-2 rounded border border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] px-3 py-2">
              <p className="text-warn">
                {node?.name} has reported version <span className="font-mono">{node?.version}</span> — it has run ozpanel,
                so it is a member. A member that is deregistered puts its own row back the next time its panel starts,
                and keeps every credential meanwhile. To remove a member, use <b>Remove</b> (eviction) on its row
                {canEvict ? "." : <> — offered only on the cluster master{masterName ? <>, currently <span className="font-mono">{masterName}</span></> : null}.</>}
              </p>
              <Toggle checked={ack} onChange={setAck} label="Deregister it anyway (registry row only)" />
            </div>
          )}
        </div>
      }
      onConfirm={() => {
        const n = node;
        if (!n) return;
        setBusy(true);
        void client
          .apiFetch<DeregisterAnswer>(`/cluster/nodes/${encodeURIComponent(n.name)}`, { method: "DELETE" })
          .then((a) => {
            const failed = a.galera?.startsWith("reconcile FAILED");
            notify({
              kind: failed ? "error" : "success",
              title: `${a.deleted} deregistered`,
              ...(failed ? { body: `Galera peer list: ${a.galera}` } : {}),
            });
            void qc.invalidateQueries({ queryKey: ["cluster-nodes"] });
            onClose();
          })
          .catch((e: unknown) => notify({ kind: "error", title: `${n.name} was not deregistered`, body: e instanceof Error ? e.message : String(e) }))
          .finally(() => setBusy(false));
      }}
    />
  );
}
